import { Text, Title } from '@components/v2/ui';
import {
	ActionIcon,
	Flex,
	Group,
	Image,
	Modal,
	Stack,
} from '@mantine/core';
import classes from './DetailsOffset.module.css';

// Images
import OffsetDetailsLogo from '@assets/images/offset-details.svg';
import { Pill } from '@components/atoms/Pill';
import { Tag } from '@components/v2/ui/Tag';
import { NOT_APPLICABLE } from '@config/constants/common';
import {
	IconExternalLink,
	IconX,
} from '@tabler/icons-react';
import {
	handleOpenTransactionExternalLink,
	truncateString,
} from '@utils/functions';
import { useTranslation } from 'react-i18next';
import { Additionalities } from './Additionalities';
import { Measurabilities } from './Measurabilities';

export const DetailsOffset = (props: {
	data: any;
	opened: boolean;
	onClose: () => void;
}) => {
	const { data, opened, onClose } = props;

	const {
		name,
		brand,
		contributionDate,
		tags,
		quantity,
		startDate,
		endDate,
		mintStatus,
		blockchain,
		transactionHash,
	} = data;

	const { t, i18n } = useTranslation();

	return (
		<Modal
			classNames={classes}
			opened={opened}
			onClose={onClose}
			size="auto"
			withCloseButton={false}
		>
			{/*Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
			>
				<Group
					wrap="nowrap"
					gap="var(--mantine-color-spacing-sm)"
				>
					<Image
						h="48px"
						w="48px"
						src={OffsetDetailsLogo}
						fit="contain"
					/>
					<Stack gap="0px">
						<Title
							order={6}
							c="var(--mantine-color-igluPrimary-9)"
						>
							{name}
						</Title>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="body2"
						>
							{t('global.brand')}{' '}
							<Text
								c="var(--mantine-color-igluTextPrimary-9)"
								variant="button"
								span
							>
								{brand}
							</Text>
						</Text>
					</Stack>
				</Group>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>
			{/*Body */}
			<Flex
				wrap={'wrap'}
				mt="48px"
				justify="space-between"
				gap={'24px'}
			>
				{/*Contribution details */}
				<Flex direction={'column'} flex={1}>
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
					>
						{t('global.offsetDetails')}
					</Text>
					{/*Content */}
					<Flex direction={'column'} gap={'8px'}>
						{/*Contribution date */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.contributionDate')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{contributionDate}
								</Text>
							</Flex>
						</Flex>
						{/*Tag */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.tag')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{tags ? (
										<Group gap={'5px'}>
											{Array.isArray(tags) &&
												tags?.map((tag: any) => (
													<div key={tag.id}>
														<Pill
															color={tag.color}
															text={
																i18n.language == 'en'
																	? tag?.value?.en ||
																		NOT_APPLICABLE
																	: tag?.value?.fr ||
																		NOT_APPLICABLE
															}
														/>
													</div>
												))}
										</Group>
									) : (
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2"
										>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Text>
							</Flex>
						</Flex>
						{/*Quantity */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.quantity')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{quantity}
								</Text>
							</Flex>
						</Flex>
						{/*Emission Start Date */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.emissionStartDate')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{startDate}
								</Text>
							</Flex>
						</Flex>
						{/*Emission End Date */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.emissionEndDate')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{endDate}
								</Text>
							</Flex>
						</Flex>
						{/*Status */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.status')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Tag
									type={mintStatus}
									label={mintStatus}
								/>
							</Flex>
						</Flex>
						{/*Blockchain */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.blockchain')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2"
								>
									{blockchain}
								</Text>
							</Flex>
						</Flex>
						{/*TransactionHash */}
						{transactionHash &&
							transactionHash != NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Group
											wrap="nowrap"
											align="center"
											justify="center"
										>
											<Text
												c="var(--mantine-color-igluTextSecondary-9)"
												variant="body2"
											>
												{t('global.transactionId')}
											</Text>
											<ActionIcon
												onClick={() =>
													handleOpenTransactionExternalLink(
														transactionHash
													)
												}
												variant="outline"
												size={'24px'}
												radius={'8px'}
												color="var(--mantine-color-igluTextPrimary-9)"
											>
												<IconExternalLink
													className={classes.label}
													size={'15px'}
													color="var(--mantine-color-igluTextPrimary-9)"
												/>
											</ActionIcon>
										</Group>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2"
										>
											{truncateString(
												transactionHash,
												18
											)}
										</Text>
									</Flex>
								</Flex>
							)}
					</Flex>
				</Flex>
				{/*Measurabilities & Additionalities projects */}
				<Flex
					flex={'2'}
					direction={'column'}
					miw={'350px'}
				>
					{/*Measurabilities */}
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
					>
						{t('projectDetails.measurabilities')}
					</Text>
					{/*Content */}
					<Flex
						className={classes.containerProject}
						direction={'column'}
						gap={'8px'}
					>
						<Measurabilities offsetId={data?.id} />
					</Flex>

					{/*Additionalities */}
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
						mt={'24px'}
					>
						{t('projectDetails.additionalities')}
					</Text>
					{/*Content */}
					<Flex
						className={classes.containerProject}
						direction={'column'}
						gap={'8px'}
					>
						<Additionalities offsetId={data?.id} />
					</Flex>
				</Flex>
			</Flex>
		</Modal>
	);
};
